import axios from 'axios';
import { getCookie, memoizeFunction } from 'utils/functions';
import createClient from './client';

const GOOGLE_BASE_PATH = 'https://maps.googleapis.com/maps/api';
const BASE_PATH = '/search/api';
// const ENRICHMENT_URL = 'https://qa-enrichment.pipl.com/enrichment';
const { ENRICHMENT_URL } = window.__CONFIG__;

export const HTTPClientSEF = createClient({ baseURL: BASE_PATH });
const HTTPClientEnrichment = createClient({ baseURL: ENRICHMENT_URL });

/**
  |--------------------------------------------------
  | Account Api Implementation
  |--------------------------------------------------
  */
export function fetchOrganizationMembers() {
  const url = '/accounts/members/';

  return HTTPClientSEF.sendRequest({ method: 'get', url });
}

export function fetchOrganizationPendingMembers() {
  const url = '/accounts/pending-members/';

  return HTTPClientSEF.sendRequest({ method: 'get', url });
}

export function isAdmin() {
  const url = '/accounts/is_admin/';

  return HTTPClientSEF.sendRequest({ method: 'get', url });
}

export function fetchOrganizationDetails() {
  const url = '/accounts/organization/';

  return HTTPClientSEF.sendRequest({ method: 'get', url });
}

export function fetchPaymentMethod() {
  const url = '/onboarding/payment-method/';

  return HTTPClientSEF.sendRequest({ method: 'get', url });
}

export function fetchLocations({ term }) {
  const url = `/locationautocomplete/?term=${term}&limit=15`;

  return HTTPClientSEF.sendRequest({ method: 'get', url, baseURL: '' });
}

export function checkEmailInviteable(email) {
  const url = '/onboarding/check-inviteable/';
  const query = { email };

  return HTTPClientSEF.sendRequest({ method: 'post', url, data: query });
}

export function checkEmail({ email }) {
  const url = '/onboarding/check-email/';
  const query = { email };

  return HTTPClientSEF.sendRequest({ method: 'post', url, data: query });
}

export function createAccount({ email, password, firstName, lastName, recaptcha }) {
  const url = '/onboarding/create-account/';
  const payload = {
    first_name: firstName,
    last_name: lastName,
    email,
    password,
    recaptcha,
  };

  return HTTPClientSEF.sendRequest({ method: 'post', url, data: payload });
}

export function createCreditCardWithSubscription({ token, card, type, period, quantity }) {
  const url = '/onboarding/create-card-and-buy/';
  const payload = {
    token,
    card,
    type,
    period,
    quantity,
  };

  return HTTPClientSEF.sendRequest({ method: 'post', url, data: payload });
}

export function createCreditCardWithSeats({ token, card, type, quantity, months }) {
  const url = '/onboarding/create-card-and-buy-seats/';
  const payload = {
    token,
    card,
    type,
    months,
    quantity,
  };

  return HTTPClientSEF.sendRequest({ method: 'post', url, data: payload });
}

export function createCreditCardWithUpgradeSeats({ token, card, type, quantity, months }) {
  const url = '/onboarding/create-card-and-upgrade-seats/';
  const payload = {
    token,
    card,
    type,
    months,
    quantity,
  };

  return HTTPClientSEF.sendRequest({ method: 'post', url, data: payload });
}

export function createCreditCard({ token, card }) {
  const url = '/onboarding/create-card/';
  const payload = {
    token,
    card,
  };

  return HTTPClientSEF.sendRequest({ method: 'post', url, data: payload });
}

/* eslint-disable camelcase */
export function updateOrganization({
  name,
  invoice_email,
  website,
  phone,
  code,
  address,
  city,
  country,
  state,
  postal_code,
}) {
  const url = '/onboarding/organization/';
  const payload = {
    name,
    invoice_email,
    website,
    phone,
    code,
    address,
    city,
    country,
    state,
    postal_code,
  };

  return HTTPClientSEF.sendRequest({ method: 'post', url, data: payload });
}

export function fetchOrganization() {
  const url = '/onboarding/organization/';

  return HTTPClientSEF.sendRequest({ method: 'get', url });
}

export function fetchOrganizationSettings({ org_id }) {
  const url = `/organization_settings/${org_id}/`;

  return HTTPClientSEF.sendRequest({ method: 'get', url });
}

export function updateOrganizationSettings({ org_id, detailed_log, delete_logs }) {
  const url = `/organization_settings/${org_id}/`;

  return HTTPClientSEF.sendRequest({
    method: 'put',
    url,
    data: {
      detailed_log,
      delete_logs,
    },
  });
}

export function uploadUsersFile({ file }) {
  const url = '/multi/upload/';

  const data = new FormData();
  data.append('file', file, file.name);

  return HTTPClientSEF.sendRequest({ method: 'post', data, url });
}

export function validateEmails({ emails }) {
  const url = '/multi/verify-emails/';
  const payload = {
    emails,
  };

  return HTTPClientSEF.sendRequest({ method: 'post', data: payload, url });
}

export function validateMultiUsers({ users, type, period }) {
  const url = '/multi/verify/';
  const payload = {
    users,
    type,
    period,
  };

  return HTTPClientSEF.sendRequest({ method: 'post', data: payload, url });
}

export function assignMultiUsers({ users, auto_password, type, period }) {
  const url = '/multi/assign/';
  const payload = {
    users,
    auto_password,
    type,
    period,
  };

  return HTTPClientSEF.sendRequest({ method: 'post', data: payload, url });
}

export function assignMultiUsersProgress() {
  const url = '/multi/progress/';

  return HTTPClientSEF.sendRequest({ method: 'get', url });
}

export function fetchAvailableSubscriptions() {
  const url = '/multi/subscriptions/';

  return HTTPClientSEF.sendRequest({ method: 'get', url });
}

/**
  |--------------------------------------------------
  | PRO HTTPClient
  |--------------------------------------------------
  */
export function getSubscription() {
  const url = '/onboarding/subscription/';

  return HTTPClientSEF.sendRequest({ method: 'get', url });
}

export function fetchVoucher() {
  const url = '/voucher/';

  return HTTPClientSEF.sendRequest({ method: 'get', url });
}

export function verifyVoucher({ voucher }) {
  const url = '/voucher/';
  const query = { voucher };

  return HTTPClientSEF.sendRequest({ method: 'post', url, data: query });
}

export function sendDemoRequest({
  full_name,
  email,
  company_name,
  job_title,
  phone_number,
  recaptcha,
  decision_maker,
  decision_maker_email,
  decision_maker_job,
}) {
  const url = '/onboarding/request-demo/';
  const payload = {
    full_name,
    email,
    company_name,
    job_title,
    phone_number,
    recaptcha,
    decision_maker,
    decision_maker_email,
    decision_maker_job,
  };

  return HTTPClientSEF.sendRequest({ method: 'post', url, data: payload });
}

export function reassignSubscription({ subscriptionId, username }) {
  const url = `/subscriptions/${subscriptionId}/reassign/`;
  const query = { username };

  return HTTPClientSEF.sendRequest({ method: 'post', url, data: query });
}

export function reassignSeat({ subscriptionId, username }) {
  const url = `/seats/${subscriptionId}/reassign/`;
  const query = { username };

  return HTTPClientSEF.sendRequest({ method: 'post', url, data: query });
}

export function createAndAssignSubscription({ subscriptionId, email, password, first_name, last_name }) {
  const url = `/subscriptions/${subscriptionId}/reassign/`;
  const payload = {
    new_user: true,
    email,
    password,
    last_name,
    first_name,
    subscriptionId,
  };

  return HTTPClientSEF.sendRequest({ method: 'post', url, data: payload });
}

export function renewalSubscription({ subscriptionId }) {
  const url = `/subscriptions/${subscriptionId}/renewal/`;

  return HTTPClientSEF.sendRequest({ method: 'post', url });
}

export function cancelSubscription({ subscriptionId }) {
  const url = `/subscriptions/${subscriptionId}/cancel/`;

  return HTTPClientSEF.sendRequest({ method: 'post', url });
}

export function updateAutoRenewal(autoRenewal, seatsAfterRenewal) {
  const url = `/seats/update_renewal/`;

  return HTTPClientSEF.sendRequest({
    method: 'post',
    url,
    data: { auto_renewal: autoRenewal, quantity: seatsAfterRenewal },
  });
}

export function changeSubscription({ id, type, period }) {
  const url = `/subscriptions/${id}/change/`;
  const query = {
    id,
    type,
    period,
  };

  return HTTPClientSEF.sendRequest({ method: 'post', url, data: query });
}

export function fetchSubscriptions({ page, filter = {} } = {}) {
  const url = '/subscriptions/';
  const query = { ...filter };

  if (typeof page !== 'undefined') {
    query.page = page + 1;
  }

  return HTTPClientSEF.sendRequest({ method: 'get', url, params: query });
}

export function fetchSeats({ page, filter = {} } = {}) {
  const url = '/seats/';
  const query = { ...filter };

  if (typeof page !== 'undefined') {
    query.page = page + 1;
  }

  return HTTPClientSEF.sendRequest({ method: 'get', url, params: query });
}

export function fetchTrials({ page, filter = {} } = {}) {
  const url = '/trials/';
  const query = { ...filter };

  if (typeof page !== 'undefined') {
    query.page = page + 1;
  }

  return HTTPClientSEF.sendRequest({ method: 'get', url, params: query });
}

export function createSubscription({ type, period, quantity = 1 }) {
  const url = '/subscriptions/';

  return HTTPClientSEF.sendRequest({ method: 'post', url, data: { type, period, quantity } });
}

export function createSeats({ quantity, months }) {
  const url = '/seats/';

  return HTTPClientSEF.sendRequest({ method: 'post', url, data: { quantity, months } });
}

export function addSeats({ quantity }) {
  const url = '/seats/upgrade/';

  return HTTPClientSEF.sendRequest({ method: 'post', url, data: { quantity } });
}

export function fetchHistory({ start, page, username, from, to }) {
  const url = '/history/';
  const query = {};

  if (start) {
    query.start = start;
  }

  if (username && username !== 'ALL') {
    query.username = username;
  }

  if (from) {
    query.from = from
      .clone()
      .utc()
      .format('X');
  }

  if (to) {
    query.to = to
      .clone()
      .utc()
      .format('X');
  }

  if (page) {
    query.page = page;
  }

  return HTTPClientSEF.sendRequest({ method: 'get', url, params: query });
}

export function fetchInvoices() {
  const url = '/invoice/';

  return HTTPClientSEF.sendRequest({ method: 'get', url });
}

export function fetchDiscounts() {
  const url = '/discount/';

  return HTTPClientSEF.sendRequest({ method: 'get', url });
}

export function applyDiscount({ code }) {
  const url = '/discount/apply/';

  return HTTPClientSEF.sendRequest({ method: 'post', url, data: { code } });
}

export function fetchOrgAddressTaxRate(orgDetails, amount, organizationId, step) {
  const url = '/tax/resolve_address/';

  return HTTPClientSEF.sendRequest({
    method: 'post',
    url,
    data: {
      location: {
        country_code: orgDetails.country_code,
        state: orgDetails.state,
        city: orgDetails.city,
        address: orgDetails.address,
        postal_code: orgDetails.postal_code,
      },
      amount,
      organization_id: organizationId,
      step,
    },
  });
}

export function fetchPrice(seats, months) {
  const url = '/seats/get_price/';

  return HTTPClientSEF.sendRequest({
    method: 'get',
    url,
    params: {
      quantity: seats,
      months,
    },
  });
}

export function fetchRenewalPrice(seats) {
  const url = '/seats/get_renewal_price/';

  return HTTPClientSEF.sendRequest({
    method: 'get',
    url,
    params: {
      quantity: seats,
    },
  });
}

export function track({ uuid, action, event, level, name, rules, additional = {} }) {
  if (typeof uuid === 'undefined' || uuid === null) {
    uuid = getCookie('_id_ut_pipl'); //eslint-disable-line
  }

  const data = {
    event,
    level,
    name,
    uuid,
    action,
    rules,
    ...additional,
  };

  return HTTPClientSEF.sendRequest({ method: 'post', url: '/tracker/track/', data, baseURL: '' });
}

export function getPossibleActions({ actions }) {
  return HTTPClientSEF.sendRequest({ method: 'get', url: '/tracker/trigger/', params: actions, baseURL: '' });
}

export function getCurrentUser() {
  return HTTPClientSEF.sendRequest({ method: 'get', url: '/current' });
}

export function search(query) {
  return HTTPClientSEF.sendRequest({ method: 'post', url: '/search/', baseURL: '', data: query, params: query });
}

export function getEnrichment({ searchHash }) {
  return HTTPClientEnrichment.sendRequest({ method: 'get', url: `/${searchHash}` });
}

export function getGeoLocation(address) {
  return axios({
    method: 'get',
    url: `${GOOGLE_BASE_PATH}/geocode/json`,
    params: { key: 'AIzaSyAOY7qKMYUZTflDb7o51IV3c_8Xg_86Hbc', address },
  });
}

export function switchOffNewVersion() {
  return HTTPClientSEF.sendRequest({
    method: 'post',
    url: 'switch/off/',
  });
}

/**
  |--------------------------------------------------
  | Sending BI Events to the server
  |--------------------------------------------------
  */
export function sendAnalyticsEvent(data) {
  return HTTPClientSEF.sendRequest({
    method: 'post',
    url: '/event_tracking/',
    data,
  });
}

/**
  |--------------------------------------------------
  | Keep session alive - empty request to the server
  |--------------------------------------------------
  */
export function keepAlive() {
  return HTTPClientSEF.sendRequest({
    method: 'get',
    url: '/keep_alive/',
    baseURL: '',
  });
}

/**
  |--------------------------------------------------
  | Lets put all in one object - easier to mock
  |--------------------------------------------------
  */

export default {
  fetchPrice,
  fetchRenewalPrice,
  fetchOrganizationMembers,
  fetchOrganizationPendingMembers,
  isAdmin,
  fetchOrganizationDetails,
  fetchPaymentMethod,
  fetchLocations,
  checkEmail,
  checkEmailInviteable,
  createAccount,
  createCreditCardWithSubscription,
  createCreditCardWithSeats,
  createCreditCardWithUpgradeSeats,
  createCreditCard,
  updateOrganization,
  fetchOrganization,

  fetchOrganizationSettings,
  updateOrganizationSettings,
  uploadUsersFile,
  validateEmails,
  validateMultiUsers,
  assignMultiUsers,
  assignMultiUsersProgress,
  fetchAvailableSubscriptions,
  fetchOrgAddressTaxRate,
  getSubscription,
  fetchVoucher,
  verifyVoucher,
  sendDemoRequest,

  reassignSubscription,
  reassignSeat,
  createAndAssignSubscription,
  renewalSubscription,
  cancelSubscription,
  updateAutoRenewal,
  changeSubscription,
  fetchSubscriptions,
  createSubscription,
  createSeats,
  addSeats,
  fetchHistory,
  fetchInvoices,
  fetchDiscounts,
  applyDiscount,

  track,
  getPossibleActions,

  switchOffNewVersion,
  getCurrentUser,
  search,
  getEnrichment,
  getGeoLocation: memoizeFunction(getGeoLocation),
  sendAnalyticsEvent,
  keepAlive,

  fetchSeats,
  fetchTrials,
};
